import React, { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { ImageIcon, Loader2, Mail, MapPin, Pen, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { z } from 'zod/v4';

import InputControl from '@/components/form/input-control';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WithStateQuery } from '@/components/ui/with-state';
import { useResponse } from '@/hooks/use-dialog';
import { cn } from '@/lib/utils';
import HookFormProvider from '@/providers/hook-form-providers';
import { fileMutations, fileQueries } from '@/queries/file/file.queries';
import { userMutations, userQueries } from '@/queries/user/user.queries';
import { noOp } from '@/services/fn';
import { getInitials, onFileRejected } from '@/services/string';
import { useDialogStore } from '@/store';

function PreviewFile({
	fileKey,
	isDirectUrl = false,
	path = 'profile-image'
}: Readonly<{
	fileKey: string,
	isDirectUrl?: boolean,
	path: 'profile-image'
}>) {
	const file = useQuery({
		...fileQueries.getPresign({ key: fileKey }),
		enabled: !isDirectUrl
	});

	if (isDirectUrl) {
		return (
			<img
				src={fileKey}
				alt={path}
				className={cn('rounded-lg object-contain shadow-sm', {
					'size-24 rounded-full': path === 'profile-image'
				})}
			/>
		);
	}
	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => (
				<img
					src={url}
					alt={path}
					className={cn('rounded-lg object-contain shadow-sm', {
						'size-24 rounded-full': path === 'profile-image'
					})}
				/>
			)}
		</WithStateQuery>
	);
}

function UpdateProfileVendor({
	photo,
	email,
	onSuccess: onSuccessUpdate
}: Readonly<{
	photo: string | null,
	email: string,
	onSuccess: () => void
}>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const [progress, setProgress] = useState<number>(0);
	const [previewUrl, setPreviewUrl] = useState<{
		key: string | null,
		isDirectUrl: boolean
	}>({ key: photo, isDirectUrl: false });
	const [fileKey, setFileKey] = useState<string | null>(photo);

	const uploadMutation = useMutation(fileMutations.uploadPresign);
	const submit = useMutation(userMutations.photo);
	const { onError } = useResponse();

	const { getRootProps, getInputProps, open, isDragAccept } = useDropzone({
		accept: {
			'image/png': ['.png'],
			'image/jpeg': ['.jpg', '.jpeg']
		},
		disabled: uploadMutation.isPending,
		noKeyboard: true,
		onDropAccepted: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			setPreviewUrl({ key: null, isDirectUrl: false });
			setProgress(0);

			const fileKey = `photo/${email}/profile.${file.name.split('.').pop()}`;

			uploadMutation.mutate(
				{
					key: fileKey,
					file: file,
					onProgress: (val) => setProgress(val)
				},
				{
					onSuccess: (sasUrl) => {
						setFileKey(fileKey);
						setPreviewUrl({ key: sasUrl, isDirectUrl: true });
					},
					onError: noOp
				}
			);
		},
		noClick: true,
		multiple: false,
		maxSize: 5_242_880,
		onDropRejected: (err) => {
			const errFile = err[0];
			if (!errFile) return;

			onError({
				message: onFileRejected({ code: errFile.errors[0]?.code, type: 'image' }),
				confirmText: 'Coba Lagi'
			});
		}
	});

	const handleReset = () => {
		setPreviewUrl({ key: null, isDirectUrl: false });
		setProgress(0);
		uploadMutation.reset();
	};

	function onSuccess({ message }: { message: string }) {
		setDialog('variant', 'success');
		setDialog('title', 'Berhasil');
		setDialog('description', message);
		setDialog('confirmText', 'OK');
		setAction('onConfirm', () => {
			onOpen(false);
			onSuccessUpdate();
		});
		setDialog('open', true);
	}

	return (
		<DialogContent>
			<DialogTitle className="flex flex-col">
				<span className="text-lg text-black">Unggah Foto Profil</span>
				<span className="text-label-input">Silakan unggah Foto Profil Anda</span>
			</DialogTitle>
			<div
				{...getRootProps({
					className: cn(
						'dropzone border-2 border-slate-300 border-dashed h-36 flex flex-col gap-1 justify-center items-center p-8 text-center rounded-lg transition delay-100 duration-300 ease-in-out',
						isDragAccept && 'bg-slate-400/50'
					)
				})}
			>
				<input {...getInputProps()} />

				{uploadMutation.isPending ? (
					<div className="z-10 flex flex-col items-center gap-2">
						<Loader2 className="size-8 animate-spin text-blue-500" />
						<span className="text-sm font-medium">Uploading... {progress}%</span>
					</div>
				) : null}

				{!uploadMutation.isPending && previewUrl.key ? (
					<div className="relative flex h-full w-full items-center justify-center p-2">
						<PreviewFile
							fileKey={previewUrl.key}
							isDirectUrl={previewUrl.isDirectUrl}
							path="profile-image"
						/>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleReset();
							}}
							className="absolute -top-2 -right-2 rounded-full border bg-white p-1 shadow-md transition hover:bg-gray-100"
						>
							<X className="size-4 text-gray-500" />
						</button>
					</div>
				) : (
					/* 3. IDLE STATE */
					!uploadMutation.isPending && (
						<>
							<div
								className="flex w-full cursor-pointer items-center justify-center rounded bg-neutral-50 p-4 transition hover:bg-neutral-100"
								onClick={open}
								role="none"
							>
								<ImageIcon className="size-6 text-gray-400" />
							</div>
							<span className="mt-4 font-medium text-black">Unggah Foto Profil</span>
							<span className="text-secondary mt-1 text-xs">
								Unggah file JPG, JPEG, PNG (maks. 5MB)
							</span>
						</>
					)
				)}
			</div>
			<div className="flex justify-end">
				<Button
					disabled={!previewUrl.key}
					type="button"
					onClick={() => {
						if (fileKey) {
							submit.mutate(
								{ profile_picture: fileKey },
								{
									onSuccess: () => onSuccess({ message: 'Foto Profil Berhasil Diubah' }),
									onError: ({ message }) => onError({ message })
								}
							);
						}
					}}
				>
					Simpan
				</Button>
			</div>
		</DialogContent>
	);
}

export function AvatarProfile({ fileKey }: Readonly<{ fileKey: string }>) {
	const file = useQuery(fileQueries.getPresign({ key: fileKey }));

	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => <AvatarImage src={url} className="h-full w-full" />}
		</WithStateQuery>
	);
}

function PhotoProfile() {
	const user = useQuery(userQueries.user());
	const [visible, setVisible] = useState<boolean>(false);

	return (
		<>
			<div className="relative size-24">
				<Avatar className="size-24 rounded-full border border-black/10">
					<WithStateQuery state={user}>
						{({ data: { profilePicture } }) =>
							profilePicture ? <AvatarProfile fileKey={profilePicture} /> : null
						}
					</WithStateQuery>
					<WithStateQuery state={user}>
						{({ data: { name } }) => (
							<AvatarFallback className="rounded-full border border-gray-400 text-3xl">
								{getInitials(name ?? '')}
							</AvatarFallback>
						)}
					</WithStateQuery>
				</Avatar>
				<div className="absolute right-0 bottom-0 size-7">
					<Button
						size={'icon'}
						variant={'ghost'}
						className="size-7 rounded-full bg-gray-300 hover:bg-gray-200"
						onClick={() => setVisible(true)}
					>
						<Pen size={12} color="#fff" />
					</Button>
				</div>
			</div>
			<Dialog open={visible} onOpenChange={setVisible}>
				<WithStateQuery state={user}>
					{({ data: { profilePicture, email } }) => (
						<UpdateProfileVendor
							photo={profilePicture}
							onSuccess={() => setVisible(false)}
							email={email}
						/>
					)}
				</WithStateQuery>
			</Dialog>
		</>
	);
}

export default function Profile() {
	const user = useQuery(userQueries.user());

	return (
		<Card className="py-0">
			<CardHeader className="flex items-center gap-4 bg-white pb-2">
				<PhotoProfile />
				<WithStateQuery state={user}>
					{({ data: { name, email, companyName } }) => (
						<div className="flex h-full flex-col text-black">
							<p className="text-2xl">{name}</p>
							<div className="text-secondary flex items-center justify-center gap-2">
								<Mail size={16} />
								<p className="text-sm">{email}</p>
								<MapPin size={16} />
								<p className="text-sm">{companyName}</p>
							</div>
						</div>
					)}
				</WithStateQuery>
			</CardHeader>
			<CardContent className="px-5">
				<Tabs defaultValue="profile" value={'profile'}>
					<div className="bg-primary-foreground flex overflow-x-auto border-b border-b-neutral-200">
						<TabsList className="border-secondary/40 rounded-none border-b-0 bg-transparent px-0 pb-0">
							<TabsTrigger
								value="profile"
								className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
							>
								<span>Data Diri</span>
							</TabsTrigger>
						</TabsList>
					</div>
					<TabsContent value="profile">
						<WithStateQuery state={user}>
							{({
								data: { name, email, companyName, businessAreaName, phoneNumber }
							}) => (
								<HookFormProvider
									schema={z.object({
										name: z.string(),
										email: z.email(),
										company: z.string(),
										businessArea: z.string(),
										phoneNumber: z.string()
									})}
									onSubmit={() => {}}
									defaultValues={{
										name,
										email,
										businessArea: businessAreaName,
										company: companyName,
										phoneNumber
									}}
								>
									<div className="grid w-full grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
										<p className="col-span-full text-xl font-medium">Data Diri</p>
										<InputControl name="name" label="Nama" isDisabled />
										<InputControl name="company" label="Perusahaan" isDisabled />
										<InputControl name="email" label="Email" isDisabled />
										<InputControl name="businessArea" label="Bisnis Area" isDisabled />
										<InputControl name="phoneNumber" label="Handphone" isDisabled />
									</div>
								</HookFormProvider>
							)}
						</WithStateQuery>
					</TabsContent>
				</Tabs>
			</CardContent>
		</Card>
	);
}
